import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBroom,
  faCalculator,
  faChartColumn,
  faChartLine,
  faChevronDown,
  faFolderClosed,
  faGears,
  faHouse,
  faMagic,
  faMoneyBillTrendUp,
  faTableColumns,
  faTicket,
  faTimes,
  faTruckFast,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { isModuleHidden } from "../config/hiddenModules";

const sidebarList = [
  {
    section: "MENU",
    sidebar: [
      {
        id: 1,
        path: "/",
        name: "Dashboard",
        icon: faHouse,
      },
      {
        id: 2,
        path: "/universalBenchmark",
        name: "Universal Benchmark",
        icon: faMagic,
        moduleKey: "benchmark",
      },
      {
        id: 4,
        path: "/dataCleaning",
        name: "Data Cleaning",
        icon: faBroom,
        moduleKey: "data-cleaning",
      },
      {
        id: 5,
        path: "/universalBenchmarkCalculator",
        name: "Universal Benchmark Calculator",
        icon: faTableColumns,
        moduleKey: "benchmark",
      },
      {
        id: 8,
        path: "/machshipTemplateAutomatic",
        name: "Machship Template Automatic",
        icon: faFolderClosed,
        moduleKey: "machship",
      },
      {
        id: 10,
        path: "/pricingPowerpoint",
        name: "Pricing Powerpoint",
        icon: faFolderClosed,
        moduleKey: "pricing",
      },
      {
        id: 11,
        path: "/reportingHub",
        name: "Reporting Hub",
        icon: faChartColumn,
        moduleKey: "reporting",
      },
      {
        id: 12,
        path: "/performanceReport",
        name: "Performance Report",
        icon: faMoneyBillTrendUp,
        moduleKey: "performance-report",
      },
      {
        id: 13,
        path: "/customerHealth",
        name: "Customer Health",
        icon: faUsers,
        moduleKey: "customer-health",
      },
      {
        id: 14,
        path: "/difotReport",
        name: "DIFOT Intelligence",
        icon: faChartLine,
        moduleKey: "difot-report",
      },
      {
        id: 15,
        path: "/jupiterPortal",
        name: "Jupiter Ops Intelligence",
        icon: faUsers,
        moduleKey: "jupiter-portal",
      },
    ],
  },
  {
    section: "Additional Features",
    sidebar: [
      {
        id: 6,
        name: "Customer Solutions",
        icon: faCalculator,
        childrens: [
          {
            id: 601,
            path: "/customerRatecardGenerator",
            name: "Customer Rate Generator",
            icon: faFolderClosed,
            moduleKey: "ratecard",
            action: "write",
          },
          {
            id: 602,
            path: "/rateCardConverter",
            name: "Ratecard Converter",
            icon: faCalculator,
            moduleKey: "ratecard",
          },
        ],
      },
      {
        id: 7,
        name: "Operational",
        icon: faGears,
        childrens: [
          {
            id: 701,
            path: "/ticketPerformance",
            name: "Ticket Performance",
            icon: faTicket,
            moduleKey: "ticket-performance",
          },
          {
            id: 702,
            path: "/kpiPerformance",
            name: "Customer Solution KPI",
            icon: faTicket,
            moduleKey: "kpi-performance",
          },
          {
            id: 703,
            path: "/customerCareKPIPerformance",
            name: "Customer Care KPI",
            icon: faTicket,
            moduleKey: "customer-care-kpi",
          },
          {
            id: 704,
            path: "/difotTools",
            name: "DIFOT Tool",
            icon: faTruckFast,
            moduleKey: "difot",
          },
          {
            id: 706,
            path: "/carrierDifotIntegration",
            name: "Carrier DIFOT Integration",
            icon: faTruckFast,
            moduleKey: "difot",
          },
          {
            id: 707,
            path: "/qbrDashboard",
            name: "QBR Dashboard",
            icon: faChartColumn,
            moduleKey: "difot",
          },
          {
            id: 705,
            path: "/deliveryRulesEngine",
            name: "Delivery Rules Engine",
            icon: faTruckFast,
            moduleKey: "delivery-rules",
          },
          {
            id: 708,
            path: "/carrierServiceMap",
            name: "Carrier Service Map",
            icon: faTruckFast,
            moduleKey: "difot",
          },
        ],
      },
    ],
  },
];

export default function Sidebar({ isSidebarOpen, onToggleSidebar, isMobile }) {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (id) => {
    setOpenMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        isSidebarOpen &&
        !event.target.closest("#sidebar") &&
        !event.target.closest("#sidebarToggle")
      ) {
        onToggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isSidebarOpen, onToggleSidebar]);

  const getSidebarClasses = () => {
    if (isMobile) {
      return `fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`;
    }

    return `fixed top-0 left-0 h-full transition-all duration-300 ${
      isSidebarOpen ? "w-64" : "w-16"
    }`;
  };

  const showNavText = isMobile ? true : isSidebarOpen;

  return (
    <>
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300"
          onClick={onToggleSidebar}
        />
      )}

      <div
        id="sidebar"
        className={`${getSidebarClasses()} border-r border-white/10 bg-gradient-to-br from-[#144677] via-[#1a5691] to-[#0d3557] overflow-y-auto overflow-x-hidden ${
          isMobile ? "" : "relative"
        }`}
      >
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="p-4 flex items-center justify-between relative z-10">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-white/95 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-3 flex-shrink-0 shadow-lg">
              <img
                src="/ATHENA_ICON_CORAL.png"
                alt="Athena logo"
                width="24"
                className="drop-shadow-lg"
              />
            </div>
            {showNavText && (
              <p className="font-bold text-xl text-white whitespace-nowrap drop-shadow-md">
                Athena
              </p>
            )}
          </div>

          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="relative z-10">
          {sidebarList.map((group) => {
            const filteredItems = group.sidebar
              .map((item) => {
                const hasItemAccess =
                  !isModuleHidden(item.moduleKey) &&
                  (!item.moduleKey || hasPermission(item.moduleKey, item.action || "read"));

                if (item.childrens?.length > 0) {
                  const filteredChildren = item.childrens
                    .map((child) => {
                      const hasChildAccess =
                        !isModuleHidden(child.moduleKey) &&
                        (!child.moduleKey ||
                          hasPermission(child.moduleKey, child.action || "read"));
                      return hasChildAccess ? child : null;
                    })
                    .filter(Boolean);

                  if (filteredChildren.length === 0 && !hasItemAccess) return null;
                  return { ...item, childrens: filteredChildren };
                }

                if (!hasItemAccess) return null;
                return item;
              })
              .filter(Boolean);

            if (!filteredItems.length) return null;

            return (
              <div key={group.section} className="mb-6">
              <div className="px-4 py-2 text-xs text-white/60 font-semibold tracking-wider">
                <span
                  className={`${!showNavText && !isMobile ? "sr-only" : ""}`}
                >
                  {showNavText || isMobile
                    ? group.section.toUpperCase()
                    : group.section
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .toUpperCase()}
                </span>
              </div>

              <div className="space-y-1 px-3">
                {filteredItems.map((item) => {
                  const isOpen =
                    openMenus[item.id] ||
                    item.childrens?.some(
                      (child) => child.path === location.pathname
                    );

                  if (item.childrens?.length > 0) {
                    return (
                      <div key={item.id}>
                        <button
                          onClick={() => toggleMenu(item.id)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-xl font-medium transition-all duration-200 ${
                            location.pathname === item.path
                              ? "bg-white/15 text-white shadow-lg"
                              : "text-white/90 hover:bg-white/10 hover:text-white hover:shadow-md"
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <FontAwesomeIcon
                              icon={item.icon}
                              className="w-5 h-5 flex-shrink-0"
                            />
                            {showNavText && (
                              <span className="truncate">{item.name}</span>
                            )}
                          </div>

                          {showNavText && (
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          )}
                        </button>

                        {isOpen && showNavText && (
                          <div className="mt-1 space-y-1 ml-2">
                            {item.childrens.map((child) => (
                              <NavLink
                                key={child.id}
                                to={child.path}
                                className={`flex items-center px-4 py-2 text-sm rounded-xl font-medium transition-all duration-200 ${
                                  location.pathname === child.path
                                    ? "bg-white/15 text-white shadow-lg"
                                    : "text-white/90 hover:bg-white/10 hover:text-white hover:shadow-md"
                                }`}
                                onClick={isMobile ? onToggleSidebar : undefined}
                              >
                                <div className="flex items-center space-x-3">
                                  {child?.icon && (
                                    <FontAwesomeIcon
                                      icon={child.icon}
                                      className="w-4 h-4 flex-shrink-0"
                                    />
                                  )}
                                  <span className="truncate">{child.name}</span>
                                </div>
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        className={`flex items-center px-3 py-2.5 text-sm rounded-xl font-medium transition-all duration-200 group relative ${
                          location.pathname === item.path
                            ? "bg-white/15 text-white shadow-lg"
                            : "text-white/90 hover:bg-white/10 hover:text-white hover:shadow-md"
                        }`}
                        onClick={isMobile ? onToggleSidebar : undefined}
                        title={!showNavText ? item.name : undefined}
                      >
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="w-5 h-5 flex-shrink-0"
                        />
                        {showNavText && (
                          <span className="ml-3 truncate">{item.name}</span>
                        )}

                        {!showNavText && !isMobile && (
                          <div className="absolute left-16 bg-gray-900/95 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-xl">
                            {item.name}
                          </div>
                        )}
                      </NavLink>
                    );
                  }
                })}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </>
  );
}
